import { msalInstance } from '../index';
import { loginRequest } from '../authConfig';

const API_BASE = '/api/v1';

async function getAccessToken() {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) return null;
    try {
        const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        return response.accessToken;
    } catch (err) {
        if (err.name === 'InteractionRequiredAuthError') {
            try {
                const response = await msalInstance.acquireTokenPopup(loginRequest);
                return response.accessToken;
            } catch { return null; }
        }
        return null;
    }
}

export async function fetchApi(path, options = {}) {
    const token = await getAccessToken();
    // Accept is explicit because the CRA dev server decides whether to proxy a
    // request to the API by looking at it — without this, an API call can be
    // answered with index.html or a 404 in development only.
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${path}`, { headers, ...options });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export const api = {
    // Work orders
    searchWorkOrders: (params) => fetchApi(`/workorders?${new URLSearchParams(params)}`),
    getWorkOrder: (pk) => fetchApi(`/workorders/${pk}`),

    // Lookups
    shops: () => fetchApi('/lookups/shops'),
    departments: () => fetchApi('/lookups/departments'),
    statuses: () => fetchApi('/lookups/statuses'),

    // PM compliance reporting
    pmLookups: () => fetchApi('/pm/lookups'),
    // Programmes only — skips the lists that exist for the bulk filter panel,
    // which roughly halves the work on the API-backed path.
    pmProgrammeList: () => fetchApi('/pm/lookups?only=pms'),
    pmProgrammes: (params) => fetchApi(`/pm/programmes?${new URLSearchParams(params || {})}`),
    pmMatrix: (params) => fetchApi(`/pm/matrix?${new URLSearchParams(params || {})}`),
    pmExceptions: (params) => fetchApi(`/pm/exceptions?${new URLSearchParams(params || {})}`),
    pmCost: (params) => fetchApi(`/pm/cost?${new URLSearchParams(params || {})}`),
    pmSites: (pmpk, params) => fetchApi(`/pm/${pmpk}/sites?${new URLSearchParams(params || {})}`),
    pmCosts: (wopks) => fetchApi(`/pm/costs?wopks=${wopks.join(',')}`),

    // Bulk issue / modify
    bulkOperations: () => fetchApi('/bulk/operations'),
    bulkPreview: (body) => fetchApi('/bulk/preview', { method: 'POST', body: JSON.stringify(body) }),
    bulkExecute: (body) => fetchApi('/bulk/execute', { method: 'POST', body: JSON.stringify(body) }),
    bulkRun: (runId) => fetchApi(`/bulk/runs/${runId}`),
    bulkCancel: (runId) => fetchApi(`/bulk/runs/${runId}/cancel`, { method: 'POST' }),
    bulkHistory: () => fetchApi('/bulk/history'),
    bulkHistoryRun: (runId) => fetchApi(`/bulk/history/${runId}`),
    bulkCsvUrl: (runId) => `/api/v1/bulk/runs/${runId}/csv`,

    // R&M dashboard
    rmLookups: () => fetchApi('/rm/lookups'),
    rmScreen: (screen, params) => fetchApi(`/rm/${screen}?${new URLSearchParams(params || {})}`),
    rmWorkOrders: (params) => fetchApi(`/rm/workorders?${new URLSearchParams(params || {})}`),

    // Email
    sendEmail: (data) => fetchApi('/email/send', { method: 'POST', body: JSON.stringify(data) }),

    // Download all attachments as zip (returns blob, not JSON)
    downloadAll: async (files, zipName) => {
        const { msalInstance } = await import('../index');
        const { loginRequest } = await import('../authConfig');
        const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        let token = null;
        if (account) {
            try { token = (await msalInstance.acquireTokenSilent({ ...loginRequest, account })).accessToken; } catch {}
        }
        const resp = await fetch('/api/v1/proxy/download-all', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ files, zipName }),
        });
        if (!resp.ok) throw new Error('Download failed');
        return resp.blob();
    },

    // Auth
    me: () => fetchApi('/auth/me'),
};
